<script setup>
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useConfig } from "@/app/composables/useConfig"
import { useI18n } from "@/app/composables/useI18n"
import { brands } from "@/app/utils/brands"
import { useTranslatedLogo } from "@/casino/composables/useBannerTranslation"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import PdfModal from "./modal/PdfModal.vue"

const { t } = useI18n()
const showPdfModal = ref(false)

const brand = import.meta.env.VITE_APP_BRAND
const { address, title, licenseToken, licenseNumber, socialLinks }
  = brands[brand]

const screenWidth = ref(window.innerWidth)

function handleResize() {
  screenWidth.value = window.innerWidth
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
  handleResize() // Initial check
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

const licenseUrl = `https://cert.gcb.cw/certificate?id=${licenseToken}`

const { isDrawerOpen, btcRate, files } = useConfig()
const { logo } = useTranslatedLogo()
const pdfLink = ref("")
const playerSafetyFiles = computed(() => {
  return files.value.filter(file => file.type === "player_safety")
})

const termsAndConditionFiles = computed(() => {
  return files.value.filter(file => file.type === "terms_and_conditions")
})

function handlePdf(link) {
  showPdfModal.value = true
  pdfLink.value = link
}

const Year = new Date().getFullYear()
</script>

<template>
  <PdfModal
    :show="showPdfModal"
    :link="pdfLink"
    @close="() => (showPdfModal = false)"
  />

  <footer data-testid="app-footer" :class="isDrawerOpen ? 'app-footer' : 'ml-313 app-footer'">
    <router-link to="/">
      <img
        :src="logo"
        alt=""
        class="brand-logo"
        :class="{ 'mobile-logo': screenWidth < 568 }"
      >
    </router-link>
    <div class="mid-footer">
      <div class="lists">
        <div class="list">
          <div class="list-heading">
            {{ t("general.play_safety") }}
          </div>
          <a
            v-for="file in playerSafetyFiles"
            :key="file.url"
            class="link-titles"
            @click="handlePdf(file.file.url)"
          >
            {{ t(`general.${file.name}`) }}
          </a>
        </div>
        <div class="list">
          <div class="list-heading">
            {{ t("general.t_and_c") }}
          </div>
          <a
            v-for="file in termsAndConditionFiles"
            :key="file.url"
            class="link-titles"
            @click="handlePdf(file.file.url)"
          >
            {{ t(`general.${file.name}`) }}
          </a>
        </div>
      </div>
      <hr v-if="screenWidth < 568" class="top-separator">
      <div class="copy-right">
        {{ t("general.copyright") }} © {{ Year }} {{ title }}. {{ t("general.all_rights_reserved") }}.
        <div class="copy-detail">
          {{ title }}.{{ t("general.title") }},
          {{ address }}, {{ t("general.address") }}.
          {{ licenseNumber }}. {{ t("general.reg_no") }}.
        </div>
        {{ t("general.crypto_trading") }}.
      </div>
    </div>
    <div class="gaming-curacao">
      <a :href="licenseUrl" target="_blank">
        <img
          src="https://seal.cgcb.info/1c0246df-1aa7-485a-a24c-21ae5e730000"
          width="150"
          height="85"
          boarder="0"
          alt="Gaming Curacao: Click to Validate"
        >
      </a>

      <BaseIcon name="18+" size="43" color="var(--be-secondary-text-color)" />
    </div>
    <hr class="top-separator">
    <div class="links-container">
      <div class="links">
        <a v-for="link in socialLinks" :key="link.src" class="link" :href="link?.url" target="_blank">
          <img

            :src="link.src"
            :alt="link.alt"
          >
        </a>
      </div>
      <div class="bitcoin-links">
        <span><img src="/assets/images/bitcoin.svg" alt="Bitcoin"></span>
        <span>1BTC={{ parseFloat(btcRate).toFixed(2) || "$0.00" }}</span>
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.app-footer {
  color: var(--be-footer-text-color);
  background-color: var(--be-bg);
  position: relative;
  margin: 51px 16px 0 16px;
  margin-left: 97px;

  .top-separator {
    margin-top: 24px;
    margin-bottom: 24px;
    border: 1px solid #213743;
  }
  .link-titles {
    color: var(--be-secondary-text-color);
    cursor: pointer;
  }

  @media screen and (max-width: 500px) {
    .top-separator {
      margin-top: 30px;
      margin-bottom: 30px;
      border: 1px solid #213743;
    }
  }

  .copy-right {
    color: var(--be-secondary-text-color);
    font-family: Rubik;
    font-size: 14px;
    margin-bottom: 24px;
    width: 50%;

    .copy-detail {
      margin: 10px 0;
    }
  }

  @media screen and (max-width: 500px) {
    .copy-right {
      width: initial;
    }
  }

  .mid-footer {
    display: flex;
    flex-direction: row-reverse;
    margin-top: 40px;
    justify-content: space-between;
    margin-right: 40px;
  }

  @media screen and (max-width: 500px) {
    .mid-footer {
      display: flex;
      flex-direction: column;
      margin-top: 0px;
      justify-content: initial;
      margin-right: 0px;
    }
  }

  .brand-logo {
    width: 171px;
  }
  .top-links {
    margin-top: 10px;
    text-align: center;
    padding-left: 20%;
    padding-right: 20%;
    font-weight: bold;
    font-size: 14px;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;

    a {
      color: var(--be-footer-text-color);
      opacity: 0.6;
      white-space: nowrap;
      transition: all 0.3s ease-in-out;
      &:hover {
        opacity: 1;
      }
      &:after {
        content: "|";
        opacity: 0.6 !important;
        margin: 10px;
        &:last-child {
          content: "";
        }
        .mobile & {
          contfooterLogoent: "";
        }
      }
    }
    &:last-child .separator {
      display: none;
    }
  }

  .lists {
    display: flex;
    justify-content: space-between;
    color: #557086;
    font-family: Rubik;
    width: 27%;

    .list {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .list-heading {
      color: var(--be-primary-btn);
      font-size: 16px;
      font-family: Rubik-med;
      cursor: pointer;
      text-transform: uppercase;
    }
  }

  @media screen and (max-width: 500px) {
    .lists {
      margin-top: 48px;
      width: initial;
    }
  }

  .bottom-text {
    margin-top: 5px;
    text-align: center;
    padding-left: 20%;
    padding-right: 20%;
    font-size: 12px;
    .tablet & {
      padding: 5%;
    }
  }

  .email {
    display: block;
    text-align: center;
    margin: 10px;
    font-size: 13px;
  }

  @media screen and (max-width: 500px) {
    .email {
      margin-bottom: 80px;
    }
  }

  .gaming-curacao {
    display: flex;
    gap: 40px;
    align-items: center;
    // justify-content: center;
  }
}

.ml-313 {
  margin-left: 313px;
}

@media screen and (max-width: 500px) {
  .app-footer {
    margin: 24px 16px 0 16px;
  }
}
.links-container {
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
}
.links {
  display: flex;
  gap: 10px;
  align-items: center;
  img {
    width: 40px;
    height: 40px;
  }
}
.bitcoin-links {
  display: flex;
  gap: 10px;
  font-size: 14px;
  background: var(--be-divider);
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  span {
    font-size: 12px;
  }
}

.link {
  cursor: pointer;
}
@media screen and (max-width: 500px) {
  .links-container {
    // position: relative;
    display: flex;
    flex-direction: column;

    margin-bottom: 80px;
    gap: 20px;
    // border-top: 1px solid #213743;
    // bottom: 90px;
  }
  .links {
    justify-content: space-between;
    gap: 0;
    img {
      width: 36px;
      height: 36px;
    }
  }
}
</style>
